import styled from '@emotion/styled'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import React, { memo } from 'react'
import * as Icon from 'react-feather'

import { Item, Props as BenefitsItem } from './Item'

export interface Props {
  benefits?: BenefitsItem[]
  description?: string
  IBEID?: number
  languageCode: string
  languagePrefix: string
}

export const Benefits = memo(function Benefits({
  benefits,
  description,
  IBEID,
  languageCode,
  languagePrefix,
}: Props) {
  if (benefits && benefits?.length < 1) {
    return null
  }

  return (
    <Container>
      <CTA
        href={`https://www.simplebooking.it/ibe/hotelbooking/search?hid=${IBEID}&lang=${
          languagePrefix ? languagePrefix.toLocaleUpperCase() : 'IT'
        }`}
        rel="noopener"
        target="_blank"
      >
        <Icon.ArrowDown />
        {useVocabularyData('book-and-save', languageCode)}
      </CTA>
      <Wrapper row space="between" wrap>
        {description ? (
          <Description dangerouslySetInnerHTML={{ __html: description }} />
        ) : null}
        <Checklist dial={3} row wrap>
          {benefits?.map((item, index) => (
            <Item key={index} {...item} />
          ))}
        </Checklist>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  max-width: 75.75rem;
  margin: -5.25rem auto 0;
  padding: 0 1.5rem;
  position: relative;
  z-index: 2;

  @media (max-width: 767px) {
    margin-top: 0;
    padding: 0;
  }
`

const CTA = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22.5rem;
  height: 5.25rem;
  background: ${({ theme }) => theme.colors.variants.neutralDark4};
  color: ${({ theme }) => theme.colors.variants.neutralLight4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.8125rem;
  font-weight: 500;
  letter-spacing: 2px;
  line-height: 1.125rem;
  padding: 0 1.5rem;
  text-transform: uppercase;
  svg {
    width: auto;
    height: 1.3125rem;
    stroke: ${({ theme }) => theme.colors.variants.primaryDark1};
    margin-right: 18px;
  }

  @media (max-width: 1023px) {
    width: 18.75rem;
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const Wrapper = styled(FlexBox)`
  position: relative;
  &:before {
    content: '';
    width: calc(100% - 11.25rem);
    height: 100%;
    border: 2px solid ${({ theme }) => theme.colors.variants.neutralLight3};
    position: absolute;
    top: 0;
    right: 0;
    z-index: -1;
  }

  @media (max-width: 1023px) {
    &:before {
      width: 100%;
    }
  }

  @media (max-width: 767px) {
    &:before {
      border-top: 0;
      border-right: 0;
      border-left: 0;
    }
  }
`

const Description = styled.div`
  width: 22.5rem;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.375rem;
  font-weight: 500;
  line-height: 2rem;
  padding: 2.9375rem 3.875rem 3.5625rem;
  b,
  strong {
    color: ${({ theme }) => theme.colors.variants.primaryDark1};
  }

  @media (max-width: 1023px) {
    width: 100%;
  }

  @media (max-width: 767px) {
    padding: 2.25rem 1.5rem;
  }
`

const Checklist = styled(FlexBox)`
  width: calc(100% - 22.5rem);
  padding: 4.625rem 0 4.875rem;

  @media (max-width: 1023px) {
    width: 100%;
    justify-content: center;
    padding: 2.25rem 0;
  }
`
